// Updated: 2026-04-07
import { Link } from 'react-router-dom';

import NewsNavbar from '@/components/feature/NewsNavbar';
import NewsFooter from '@/components/feature/NewsFooter';
import { useAllNews, useFeaturedNews, usePopularNews, useCategories } from '@/hooks/useNews';
import { useSEO } from '@/hooks/useSEO';

export default function NewsHomePage() {
  const { news: featuredNews } = useFeaturedNews();
  const { news: popularNews } = usePopularNews(5);
  const { news: latestNews } = useAllNews(6);
  const { categories } = useCategories();

  useSEO({
    title: 'TeknoNovaHub | Türkiye\'nin Teknoloji Haber Merkezi - Yapay Zeka, Yazılım, Girişimcilik',
    description: 'TeknoNovaHub - Türkiye\'nin en kapsamlı teknoloji haber sitesi. Yapay zeka, yazılım geliştirme, girişimcilik, donanım, oyun ve siber güvenlik alanlarında güncel haberler ve analizler.',
    keywords: 'teknoloji haberleri, yapay zeka, yazılım, girişimcilik, donanım, siber güvenlik, TeknoNovaHub',
    url: '/haberler',
    type: 'website',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Son Teknoloji Haberleri',
      description: 'TeknoNovaHub\'dan en güncel teknoloji haberleri',
      url: 'https://teknonovahub.com/haberler',
      numberOfItems: latestNews.length,
    },
  });

  return (
    <div className="min-h-screen bg-[#0A0E27] font-sans">
      <NewsNavbar />

      <main className="pt-28 md:pt-32">
        {/* Hero Section */}
        <section className="max-w-7xl mx-auto px-6 mb-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {featuredNews[0] && (
              <Link
                to={`/haber/${featuredNews[0].id}`}
                className="lg:col-span-2 relative group overflow-hidden rounded-2xl cursor-pointer"
              >
                <div className="aspect-[16/9] lg:aspect-[2/1]">
                  <img
                    src={featuredNews[0].image}
                    alt={featuredNews[0].title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E27] via-[#0A0E27]/50 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                  <span className="inline-block px-3 py-1 bg-[#00D9FF] text-[#0A0E27] text-xs font-bold rounded-full mb-3">
                    {categories.find(c => c.id === featuredNews[0].category)?.name}
                  </span>
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-black text-white leading-tight mb-3">
                    {featuredNews[0].title}
                  </h1>
                  <p className="text-white/60 text-sm md:text-base line-clamp-2 mb-4">
                    {featuredNews[0].excerpt}
                  </p>
                  <div className="flex items-center gap-4 text-white/40 text-xs">
                    <span>{featuredNews[0].author}</span>
                    <span>·</span>
                    <span>{featuredNews[0].date}</span>
                    <span>·</span>
                    <span>{featuredNews[0].read_time} okuma</span>
                  </div>
                </div>
              </Link>
            )}

            <div className="flex flex-col gap-4">
              {featuredNews.slice(1, 3).map((item) => (
                <Link
                  key={item.id}
                  to={`/haber/${item.id}`}
                  className="relative group overflow-hidden rounded-xl flex-1 cursor-pointer"
                >
                  <div className="h-full min-h-[140px]">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E27] via-[#0A0E27]/60 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <span className="inline-block px-2 py-0.5 bg-[#00FF88] text-[#0A0E27] text-[10px] font-bold rounded-full mb-2">
                      {categories.find(c => c.id === item.category)?.name}
                    </span>
                    <h3 className="text-sm md:text-base font-bold text-white line-clamp-2">
                      {item.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="max-w-7xl mx-auto px-6 mb-12">
          <div className="flex items-center gap-3 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/kategori/${cat.id}`}
                className="flex items-center gap-2 px-4 py-2.5 bg-white/5 border border-white/10 rounded-full hover:bg-white/10 transition-all duration-300 cursor-pointer whitespace-nowrap group"
              >
                <span
                  className="w-8 h-8 flex items-center justify-center rounded-full transition-colors"
                  style={{ backgroundColor: `${cat.color}20` }}
                >
                  <i className={`${cat.icon} text-sm`} style={{ color: cat.color }} />
                </span>
                <span className="text-white text-sm font-medium">{cat.name}</span>
                <i className="ri-arrow-right-line text-white/40 group-hover:text-white transition-colors text-sm" />
              </Link>
            ))}
          </div>
        </section>

        {/* Latest News */}
        <section className="max-w-7xl mx-auto px-6 mb-16">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-bold text-white">Son Haberler</h2>
            <Link to="/haberler" className="text-[#00D9FF] text-sm font-medium hover:underline cursor-pointer">
              Tümünü Gör
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {latestNews.map((item) => (
              <Link key={item.id} to={`/haber/${item.id}`} className="group cursor-pointer">
                <div className="relative overflow-hidden rounded-xl mb-4">
                  <div className="aspect-[16/10]">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <span
                    className="absolute top-3 left-3 px-2 py-1 text-[10px] font-bold rounded-full"
                    style={{
                      backgroundColor: `${categories.find(c => c.id === item.category)?.color}20`,
                      color: categories.find(c => c.id === item.category)?.color,
                    }}
                  >
                    {categories.find(c => c.id === item.category)?.name}
                  </span>
                </div>
                <h3 className="text-white font-bold text-base mb-2 line-clamp-2 group-hover:text-[#00D9FF] transition-colors">
                  {item.title}
                </h3>
                <p className="text-white/50 text-sm line-clamp-2 mb-3">{item.excerpt}</p>
                <div className="flex items-center gap-3 text-white/40 text-xs">
                  <span>{item.date}</span>
                  <span>·</span>
                  <span>{item.read_time} okuma</span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Trending */}
        <section className="bg-white/5 border-y border-white/10 py-12">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center gap-2 mb-6">
              <i className="ri-fire-line text-[#FF6B6B] text-xl" />
              <h2 className="text-xl md:text-2xl font-bold text-white">Trend Haberler</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {popularNews.map((item, index) => (
                <Link key={item.id} to={`/haber/${item.id}`} className="group cursor-pointer">
                  <div className="relative overflow-hidden rounded-lg mb-3">
                    <div className="aspect-[4/3]">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="absolute top-2 left-2 w-6 h-6 flex items-center justify-center bg-[#00D9FF] text-[#0A0E27] text-xs font-bold rounded-full">
                      {index + 1}
                    </div>
                  </div>
                  <h3 className="text-white font-semibold text-sm line-clamp-2 group-hover:text-[#00D9FF] transition-colors">
                    {item.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-2 text-white/40 text-xs">
                    <i className="ri-eye-line" />
                    <span>{item.views.toLocaleString()}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="max-w-7xl mx-auto px-6 py-16">
          <div className="bg-gradient-to-r from-[#00D9FF]/20 to-[#00FF88]/20 rounded-2xl p-8 md:p-12 text-center border border-white/10">
            <i className="ri-mail-send-line text-4xl text-[#00D9FF] mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Günlük Teknoloji Bülteni</h2>
            <p className="text-white/60 text-base max-w-md mx-auto mb-6">
              En güncel teknoloji haberlerini her sabah e-posta kutunuza gönderelim.
            </p>
            <Link
              to="/abonelik"
              className="inline-flex items-center gap-2 px-8 py-3 bg-[#00D9FF] text-[#0A0E27] font-bold rounded-full hover:bg-[#00D9FF]/90 transition-all duration-300 cursor-pointer"
            >
              Ücretsiz Abone Ol
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </section>
      </main>

      <NewsFooter />
    </div>
  );
}
